import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Plus, X, Globe, Mail } from 'lucide-react';
import { AdminTenantFormData } from './types';

interface Props {
  data: AdminTenantFormData;
  updateData: (updates: Partial<AdminTenantFormData>) => void;
}

const StepWebsiteDeveloper: React.FC<Props> = ({ data, updateData }) => {
  const addUrl = () => {
    updateData({ additionalUrls: [...data.additionalUrls, ''] });
  };

  const removeUrl = (index: number) => {
    const updated = data.additionalUrls.filter((_, i) => i !== index);
    updateData({ additionalUrls: updated });
  };

  const updateUrl = (index: number, value: string) => {
    const updated = [...data.additionalUrls];
    updated[index] = value;
    updateData({ additionalUrls: updated });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="websiteUrl">
          Primary Website URL <span className="text-destructive">*</span>
        </Label>
        <div className="relative">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="websiteUrl"
            value={data.websiteUrl}
            onChange={(e) => updateData({ websiteUrl: e.target.value })}
            placeholder="https://example.com"
            className="pl-10"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Additional URLs</Label>
        <div className="space-y-2">
          {data.additionalUrls.map((url, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={url}
                onChange={(e) => updateUrl(index, e.target.value)}
                placeholder="https://..."
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeUrl(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addUrl}
            className="mt-2"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add URL
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="developerEmail">
            Developer/IT Email <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="developerEmail"
              type="email"
              value={data.developerEmail}
              onChange={(e) => updateData({ developerEmail: e.target.value })}
              placeholder="dev@example.com"
              className="pl-10"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="summaryForwardEmail">
            Summary Forward Email <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="summaryForwardEmail"
              type="email"
              value={data.summaryForwardEmail}
              onChange={(e) => updateData({ summaryForwardEmail: e.target.value })}
              placeholder="inquiries@example.com"
              className="pl-10"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Install Scope</Label>
        <RadioGroup
          value={data.installScope}
          onValueChange={(value: 'site-wide' | 'specific-pages') => updateData({ installScope: value })}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="site-wide" id="site-wide" />
            <Label htmlFor="site-wide" className="font-normal cursor-pointer">
              Site-wide
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="specific-pages" id="specific-pages" />
            <Label htmlFor="specific-pages" className="font-normal cursor-pointer">
              Specific pages only
            </Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
};

export default StepWebsiteDeveloper;
